import { request } from "./http";

/** The period a figure covers. `days = null` means everything the service will show. */
export interface Window {
  since: string | null;
  until: string | null;
}

export interface ProjectStats {
  project_id: string;
  memories: number;
  /** True when paging stopped at the ceiling, so `memories` is a floor. */
  capped: boolean;
  by_type: Record<string, number>;
  by_agent: Record<string, number>;
  active: number;
  superseded: number;
  /** Memories not embedded yet — the number that explains a recall finding nothing. */
  embedding_pending: number;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  llm_calls: number;
  first_seen: string | null;
  last_seen: string | null;
  problem: string | null;
}

export interface DailyPoint {
  day: string;
  memories: number;
  by_type: Record<string, number>;
}

export interface StatsReport {
  window: Window;
  tenant_id: string;
  projects: ProjectStats[];
  daily: DailyPoint[];
  memories: number;
  capped: boolean;
  total_tokens: number;
  llm_calls: number;
  by_type: Record<string, number>;
  by_agent: Record<string, number>;
  page_size: number;
  max_pages: number;
  note: string | null;
}

export interface ActivityRow {
  id: string;
  project_id: string | null;
  type: string;
  status: string;
  embedding_status: string | null;
  importance: number | null;
  agent_id: string | null;
  subject: string | null;
  summary: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ActivityPage {
  rows: ActivityRow[];
  /** Project -> cursor for its next slice. A project that is absent is exhausted. */
  cursors: Record<string, string>;
  projects: string[];
  problems: string[];
}

export interface CallLogEntry {
  call_id: string;
  operation_id: string;
  task_label: string;
  method: string;
  url: string;
  status: number;
  ok: boolean;
  duration_ms: number;
  profile_used: string | null;
  request_id: string | null;
  problem_title: string | null;
  started_at: string;
}

export interface CallLogResponse {
  calls: CallLogEntry[];
  capacity: number;
}

/** Filters the activity list passes straight through to the service. */
export interface ActivityQuery {
  projects?: string[];
  days?: number;
  memory_type?: string;
  memory_status?: string;
  min_importance?: number;
  /** Project -> cursor, from the previous page. Sent as repeated `cursor=project:token`. */
  cursors?: Record<string, string>;
  limit?: number;
}

function query(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") {
      search.set(key, String(value));
    }
  }
  const rendered = search.toString();
  return rendered ? `?${rendered}` : "";
}

export function fetchStats(days?: number, projects?: string[]): Promise<StatsReport> {
  return request<StatsReport>(
    `/api/stats${query({ days, projects: projects?.join(",") })}`,
  );
}

export function fetchActivity(params: ActivityQuery): Promise<ActivityPage> {
  const { projects, cursors, ...rest } = params;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(rest)) {
    if (value !== undefined && value !== "") {
      search.set(key, String(value));
    }
  }
  if (projects?.length) {
    search.set("projects", projects.join(","));
  }
  // One parameter per project: cursors are opaque per-project tokens, so there is nothing
  // sensible to merge them into.
  for (const [project, token] of Object.entries(cursors ?? {})) {
    search.append("cursor", `${project}:${token}`);
  }
  const rendered = search.toString();
  return request<ActivityPage>(`/api/activity${rendered ? `?${rendered}` : ""}`);
}

export function fetchCallLog(): Promise<CallLogResponse> {
  return request<CallLogResponse>("/api/calls");
}
